import type { AttendantStatus, DocID } from "@/types";

/**
 * O que o socket já disse de cada conversa (SA-1452): o último status e
 * quando chegou o último `updated_chat`.
 *
 * - Status: o `updated_chat` refazia a contagem do menu sempre que a conversa
 *   não estava em nenhum cache ("não sei de onde veio" contava como mudança).
 *   Fora da tela de conversas isso era uma recarga por mensagem. Com o status
 *   lembrado, só a primeira mensagem de cada conversa desconhecida refaz.
 * - Hora: a ação do atendente (assumir, encerrar, transferir) espera o evento
 *   da própria conversa antes de recarregar as listas inteiras: se ele chegou,
 *   o socket já moveu a linha.
 *
 * Só ids e status, em memória; some no F5.
 */
const MAX_TRACKED_CHATS = 5000;

type Entry = { status: AttendantStatus | undefined; at: number };
type Waiter = {
  chatId: DocID;
  sinceMs: number;
  expectedStatus?: AttendantStatus;
  done: (seen: boolean) => void;
};

const matches = (
  status: AttendantStatus | undefined,
  expectedStatus: AttendantStatus | undefined
) => expectedStatus === undefined || status === expectedStatus;

const entries = new Map<DocID, Entry>();
const waiters = new Set<Waiter>();

export function recordUpdatedChat(
  chatId: DocID,
  status: AttendantStatus | undefined
): void {
  const at = Date.now();
  // Reinsere para a ordem do Map ser a do evento mais recente.
  entries.delete(chatId);
  entries.set(chatId, { status, at });
  if (entries.size > MAX_TRACKED_CHATS) {
    const oldest = entries.keys().next().value;
    if (oldest !== undefined) entries.delete(oldest);
  }
  for (const waiter of [...waiters]) {
    if (
      waiter.chatId === chatId &&
      at >= waiter.sinceMs &&
      matches(status, waiter.expectedStatus)
    ) {
      waiter.done(true);
    }
  }
}

export const knownChatStatus = (chatId: DocID): AttendantStatus | undefined =>
  entries.get(chatId)?.status;

/**
 * Esquece o status de todas as conversas (a hora do último evento fica). Na
 * reconexão do socket: o que mudou enquanto ele estava fora não passou por
 * aqui, e um status velho faria a contagem do menu perder a próxima transição.
 */
export function forgetChatStatuses(): void {
  for (const entry of entries.values()) entry.status = undefined;
}

/** Esquece o status de uma conversa (saiu de vista: `chat_hidden`). */
export function forgetChatStatus(chatId: DocID): void {
  const entry = entries.get(chatId);
  if (entry) entry.status = undefined;
}

export const lastUpdatedChatAt = (chatId: DocID): number | undefined =>
  entries.get(chatId)?.at;

/**
 * `true` se um `updated_chat` da conversa chegou desde `sinceMs`. Com
 * `expectedStatus`, só conta o evento com esse status: uma mensagem do cliente
 * no meio da ação traz o status de antes e não é o evento da ação.
 */
export function waitForUpdatedChat(
  chatId: DocID,
  sinceMs: number,
  timeoutMs: number,
  expectedStatus?: AttendantStatus
): Promise<boolean> {
  const latest = entries.get(chatId);
  if (
    latest &&
    latest.at >= sinceMs &&
    matches(latest.status, expectedStatus)
  ) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const waiter: Waiter = {
      chatId,
      sinceMs,
      expectedStatus,
      done: (seen) => {
        clearTimeout(timer);
        waiters.delete(waiter);
        resolve(seen);
      },
    };
    const timer = setTimeout(() => waiter.done(false), timeoutMs);
    waiters.add(waiter);
  });
}

export function resetChatEventLedger(): void {
  entries.clear();
  for (const waiter of [...waiters]) waiter.done(false);
}
